// src/pages/support/SupportTicketPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useApi from '../../hooks/useApi';
import { getSupportTicketView, postSupportReply } from '../../api/client';
import { formatDate, getError } from '../../utils/helpers';
import { PageLoader } from '../../components/common';
import './SupportTicketPage.css';

function isAdminReply(r) {
  const by = (r.replied_by || '').toLowerCase();
  return !!by && by !== 'user' && by !== 'you';
}

const STATUS_CFG = {
  open:     { label: 'Open',     cls: 'stk-badge--open'     },
  answered: { label: 'Answered', cls: 'stk-badge--answered' },
  closed:   { label: 'Closed',   cls: 'stk-badge--closed'   },
};

function getStatus(raw) {
  return STATUS_CFG[String(raw || '').toLowerCase()] || STATUS_CFG.open;
}

// Group messages by date
function groupByDate(messages) {
  const groups = [];
  let lastDate = null;
  messages.forEach((msg) => {
    const d = msg.date ? new Date(msg.date).toDateString() : '';
    const today     = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const label = d === today ? 'Today' : d === yesterday ? 'Yesterday' : (msg.date ? new Date(msg.date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
    if (d !== lastDate) { groups.push({ type: 'date', label }); lastDate = d; }
    groups.push({ type: 'msg', ...msg });
  });
  return groups;
}

export default function SupportTicketPage() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const fetcher   = () => getSupportTicketView({ ticket_id: Number(id) });
  const { data, loading, error, refetch } = useApi(fetcher, [id]);

  const [reply,  setReply]  = useState('');
  const [saving, setSaving] = useState(false);
  const threadRef  = useRef(null);
  const textareaRef = useRef(null);

  const ticket   = data?.ticket || {};
  const replies  = ticket.replies || [];
  const status   = getStatus(ticket.status);
  const isClosed = String(ticket.status || '').toLowerCase() === 'closed';

  // Build full message list: original + replies
  const allMessages = [
    ticket.message ? { id: 'orig', replied_by: 'user', reply: ticket.message, date: ticket.date, isOrig: true } : null,
    ...replies,
  ].filter(Boolean);

  const grouped = groupByDate(allMessages);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [replies.length, loading]);

  // Auto-grow textarea
  const handleTextareaChange = (e) => {
    setReply(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const handleReply = async () => {
    const msg = reply.trim();
    if (!msg) { toast.error('Write a reply first.'); return; }
    if (msg.length < 10) { toast.error('Reply must be at least 10 characters.'); return; }
    setSaving(true);
    try {
      const { data: res } = await postSupportReply({ ticket_id: Number(id), message: msg });
      if (res.status === 'success') {
        setReply('');
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        refetch();
      } else toast.error(res.msg || 'Failed to send reply.');
    } catch (err) { toast.error(getError(err)); }
    finally { setSaving(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }
  };

  if (loading) return <PageLoader text="Loading ticket..." />;
  if (error)   return <div className="page-loader" style={{ color: 'var(--danger)' }}>{error}</div>;

  return (
    <div className="stk-screen">

      {/* ── Fixed Chat Header ── */}
      <div className="stk-header">
        <button className="stk-back-btn" onClick={() => navigate('/support')}>
          <i className="fas fa-arrow-left" />
        </button>
        <div className="stk-header-avatar">
          <i className="fas fa-headset" />
        </div>
        <div className="stk-header-info">
          <span className="stk-header-name">Support Agent</span>
          <div className="stk-header-meta">
            <span className="stk-header-ticket">Ticket #{ticket.id || id}</span>
            <span className={`stk-badge ${status.cls}`}>{status.label}</span>
          </div>
        </div>
        {ticket.ref && ticket.ref !== 'GENERAL' && (
          <div className="stk-header-ref" onClick={() => { navigator.clipboard.writeText(ticket.ref); toast.success('Ref copied!'); }}>
            <i className="fas fa-receipt" />
            <span>{ticket.ref}</span>
            <i className="fas fa-copy stk-ref-copy" />
          </div>
        )}
        <button className="stk-refresh-btn" onClick={refetch}>
          <i className="fas fa-rotate-right" />
        </button>
      </div>

      {/* ── Scrollable Thread ── */}
      <div className="stk-thread" ref={threadRef}>
        {grouped.length === 0 ? (
          <div className="stk-empty">
            <div className="stk-empty-icon"><i className="fas fa-clock" /></div>
            <p>Awaiting reply from our support team.</p>
          </div>
        ) : grouped.map((item, idx) => {
          if (item.type === 'date') {
            return (
              <div key={`date-${idx}`} className="stk-date-divider">
                <span>{item.label}</span>
              </div>
            );
          }
          const admin = isAdminReply(item);
          return (
            <div key={item.id || idx} className={`stk-bubble-row ${admin ? 'stk-bubble-row--admin' : 'stk-bubble-row--user'}`}>
              {admin && (
                <div className="stk-avatar">
                  <i className="fas fa-headset" />
                </div>
              )}
              <div className={`stk-bubble ${admin ? 'stk-bubble--admin' : 'stk-bubble--user'}`}>
                {admin && <span className="stk-bubble-name">{item.replied_by || 'Support Agent'}</span>}
                {item.image && (
                  <img src={item.image} alt="attachment" className="stk-bubble-img"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <p>{item.reply || item.message}</p>
                <div className="stk-bubble-footer">
                  <span className="stk-bubble-time">{item.date ? new Date(item.date).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  {!admin && <i className="fas fa-check-double stk-delivered" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Bottom Input Bar ── */}
      {isClosed ? (
        <div className="stk-closed-bar">
          <i className="fas fa-lock" />
          <span>This ticket is closed. Open a new ticket if you need further help.</span>
        </div>
      ) : (
        <div className="stk-input-bar">
          <textarea
            ref={textareaRef}
            className="stk-input"
            rows={1}
            placeholder="Type a message..."
            value={reply}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
          />
          <button
            className="stk-send-btn"
            onClick={handleReply}
            disabled={saving || reply.trim().length < 10}
          >
            {saving
              ? <span className="spinner" style={{ width: 16, height: 16, borderColor: '#fff #fff transparent' }} />
              : <i className="fas fa-paper-plane" />}
          </button>
        </div>
      )}

    </div>
  );
}